import React, { useState } from 'react';
import { ExploreWithAI } from '@explore-with-ai/widget';
import { ExploreConfig } from '@explore-with-ai/core';
import './DemoPage.css';

interface DemoPageProps {
  onBackToHome: () => void;
  onGetStarted: () => void;
}

const DemoPage: React.FC<DemoPageProps> = ({ onBackToHome, onGetStarted }) => {
  const [selectedDemo, setSelectedDemo] = useState<string>('stock-market');

  // Demo configurations for different use cases
  const demoConfigs: Record<string, ExploreConfig> = {
    'federal-reserve': {
      id: 'demo-fed-data',
      title: 'Federal Reserve Economic Data',
      description: 'Explore key economic indicators including interest rates, employment data, and monetary policy decisions from the Federal Reserve.',
      sourceName: 'Federal Reserve Economic Data (FRED)',
      sourceUrl: 'https://fred.stlouisfed.org/series/FEDFUNDS',
      format: 'Interactive Dashboard',
      lastUpdated: 'Updated daily',
      fields: [],
      modes: ['general', 'trends', 'anomalies', 'policy'],
      enabledPlatforms: ['chatgpt', 'claude', 'gemini', 'perplexity'],
      buttonStyle: 'default',
      audience: 'policy'
    },
    'climate-data': {
      id: 'demo-climate',
      title: 'Global Climate Change Indicators',
      description: 'Analyze temperature trends, CO2 levels, and climate patterns from NASA and NOAA climate monitoring systems.',
      sourceName: 'NASA Climate Change and Global Warming',
      sourceUrl: 'https://climate.nasa.gov/evidence/',
      format: 'Time Series Visualization',
      lastUpdated: 'Updated monthly',
      fields: [],
      modes: ['general', 'trends', 'anomalies'],
      enabledPlatforms: ['chatgpt', 'claude', 'gemini'],
      buttonStyle: 'default',
      audience: 'researcher'
    },
    'census-data': {
      id: 'demo-census',
      title: 'U.S. Census Demographics',
      description: 'Explore population demographics, housing data, and economic characteristics from the U.S. Census Bureau.',
      sourceName: 'U.S. Census Bureau',
      sourceUrl: 'https://data.census.gov/',
      format: 'Interactive Maps & Charts',
      lastUpdated: 'Updated annually',
      fields: [],
      modes: ['general', 'trends', 'policy'],
      enabledPlatforms: ['chatgpt', 'claude', 'perplexity', 'grok'],
      buttonStyle: 'default',
      audience: 'journalist'
    },
    'stock-market': {
      id: 'demo-stocks',
      title: 'Yahoo Finance Market Data',
      description: 'Explore market indices, stock prices, and financial indicators for investment research and business analysis.',
      sourceName: 'Yahoo Finance',
      sourceUrl: 'https://finance.yahoo.com/',
      format: 'Real-time Dashboard',
      lastUpdated: 'Updated in real-time',
      fields: [],
      modes: ['general', 'trends', 'anomalies'],
      enabledPlatforms: ['chatgpt', 'claude', 'gemini', 'perplexity'],
      buttonStyle: 'default',
      audience: 'general'
    }
  };

  const demoDescriptions = {
    'federal-reserve': {
      title: 'Government & Policy Data',
      subtitle: 'Economic indicators and policy analysis',
      icon: '🏛️',
      useCase: 'Perfect for policy researchers, economists, and journalists covering financial news.'
    },
    'climate-data': {
      title: 'Scientific Research Data',
      subtitle: 'Climate and environmental monitoring',
      icon: '🌍',
      useCase: 'Ideal for researchers, environmental journalists, and policy makers.'
    },
    'census-data': {
      title: 'Demographic & Social Data',
      subtitle: 'Population and housing statistics',
      icon: '📊',
      useCase: 'Great for journalists, urban planners, and social researchers.'
    },
    'stock-market': {
      title: 'Financial Market Data',
      subtitle: 'Investment and trading analysis',
      icon: '📈',
      useCase: 'Useful for investors, financial analysts, and business reporters.'
    }
  };

  const handleAnalytics = (event: any) => {
    console.log('Demo Analytics:', event);
  };

  return (
    <div className="demo-page">
      {/* Header */}
      <header className="demo-page__header">
        <div className="demo-page__container">
          <button className="demo-page__back-button" onClick={onBackToHome}>
            ← Back to Home
          </button>
          <div className="demo-page__header-content">
            <h1 className="demo-page__title">Interactive Demo</h1>
            <p className="demo-page__subtitle">
              Experience how Faathum's Explore with AI widgets work with real data examples
            </p>
          </div>
        </div>
      </header>

      {/* Demo Selection */}
      <section className="demo-page__selection">
        <div className="demo-page__container">
          <h2>Choose a Demo Scenario</h2>
          <div className="demo-page__scenarios">
            {Object.entries(demoDescriptions).map(([key, demo]) => (
              <button
                key={key}
                className={`demo-page__scenario ${selectedDemo === key ? 'active' : ''}`}
                onClick={() => setSelectedDemo(key)}
              >
                <div className="demo-page__scenario-icon">{demo.icon}</div>
                <div className="demo-page__scenario-content">
                  <h3>{demo.title}</h3>
                  <p>{demo.subtitle}</p>
                  <small>{demo.useCase}</small>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Live Demo */}
      <section className="demo-page__live">
        <div className="demo-page__container">
          <div className="demo-page__demo-layout">
            <div className="demo-page__demo-info">
              <h3>Live Widget Demo</h3>
              <p>
                This is a fully functional Explore with AI widget using real configuration. 
                Try different analysis modes and see how the AI prompts are customized for 
                different audiences.
              </p>
              
              <div className="demo-page__features">
                <div className="demo-page__feature">
                  <strong>🎯 Audience Targeting:</strong>
                  <span>Prompts tailored for {demoConfigs[selectedDemo]?.audience}</span>
                </div>
                <div className="demo-page__feature">
                  <strong>🔍 Analysis Modes:</strong>
                  <span>{demoConfigs[selectedDemo]?.modes?.join(', ') || 'N/A'}</span>
                </div>
                <div className="demo-page__feature">
                  <strong>🤖 AI Platforms:</strong>
                  <span>{demoConfigs[selectedDemo]?.enabledPlatforms?.length || 0} platforms supported</span>
                </div>
              </div>

              <div className="demo-page__cta">
                <button className="demo-page__cta-button" onClick={onGetStarted}>
                  Create Your Own Widget
                </button>
              </div>
            </div>

            <div className="demo-page__widget-container">
              <div className="demo-page__widget-frame">
                <div className="demo-page__widget-header">
                  <div className="demo-page__widget-title">
                    {demoDescriptions[selectedDemo as keyof typeof demoDescriptions].title}
                  </div>
                  <div className="demo-page__widget-badge">Live Demo</div>
                </div>
                
                <ExploreWithAI
                  config={demoConfigs[selectedDemo]}
                  onAnalytics={handleAnalytics}
                  className="demo-page__widget"
                />
                
                <div className="demo-page__widget-footer">
                  <small>
                    This widget is embedded using a simple HTML snippet generated by Faathum
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="demo-page__how-it-works">
        <div className="demo-page__container">
          <h2>How It Works</h2>
          <div className="demo-page__steps">
            <div className="demo-page__step">
              <div className="demo-page__step-number">1</div>
              <div className="demo-page__step-content">
                <h4>Configure Your Dataset</h4>
                <p>Add your data source information, description, and target audience</p>
              </div>
            </div>
            <div className="demo-page__step">
              <div className="demo-page__step-number">2</div>
              <div className="demo-page__step-content">
                <h4>Customize Analysis Options</h4>
                <p>Choose which analysis modes and AI platforms to enable</p>
              </div>
            </div>
            <div className="demo-page__step">
              <div className="demo-page__step-number">3</div>
              <div className="demo-page__step-content">
                <h4>Generate & Deploy</h4>
                <p>Get the embed code and add the widget to your website</p>
              </div>
            </div>
            <div className="demo-page__step">
              <div className="demo-page__step-number">4</div>
              <div className="demo-page__step-content">
                <h4>Users Explore with AI</h4>
                <p>Visitors get contextual prompts and seamless AI platform integration</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Code Example */}
      <section className="demo-page__code">
        <div className="demo-page__container">
          <h2>Simple Integration</h2>
          <p>Add an Explore with AI widget to any website with just a few lines of code:</p>
          
          <div className="demo-page__code-block">
            <pre><code>{`<!-- Add to your HTML -->
<div id="explore-with-ai-widget"></div>

<script src="https://cdn.faathum.com/widget.js"></script>
<script>
  FaathumWidget.create({
    target: '#explore-with-ai-widget',
    config: {
      title: '${demoConfigs[selectedDemo].title}',
      description: '${demoConfigs[selectedDemo].description}',
      sourceName: '${demoConfigs[selectedDemo].sourceName}',
      sourceUrl: '${demoConfigs[selectedDemo].sourceUrl}',
      audience: '${demoConfigs[selectedDemo].audience}'
    }
  });
</script>`}</code></pre>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="demo-page__final-cta">
        <div className="demo-page__container">
          <div className="demo-page__cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Create your own widget that advances how data is understood in minutes</p>
            <button className="demo-page__cta-primary" onClick={onGetStarted}>
              Start Building Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemoPage;
